import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useI18n } from '../i18n/LanguageContext';
import { Reveal } from '../components/Reveal';

const IMG_HERO = 'https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?auto=format&fit=crop&w=2000&q=80';

export default function International() {
  const { t } = useI18n();
  return (
    <div data-testid="international-page">
      {/* Hero */}
      <section className="relative min-h-[62vh] flex items-end overflow-hidden" data-testid="intl-hero">
        <img src={IMG_HERO} alt="Global industrial reach" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest/70 to-forest/30" />
        <div className="container-x relative z-10 py-20 md:py-28 text-sand">
          <Reveal className="kicker !text-rust">{t.international.kicker}</Reveal>
          <Reveal delay={100}>
            <h1 className="display-1 mt-6 text-sand max-w-[20ch] text-balance">{t.international.title}</h1>
          </Reveal>
        </div>
      </section>

      {/* Intro */}
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-20">
          <div className="md:col-span-4">
            <div className="kicker">— 4 continents</div>
          </div>
          <Reveal delay={80} className="md:col-span-8">
            <p className="lead">{t.international.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Regions */}
      <section className="bg-sand-warm">
        <div className="container-x py-20 md:py-28">
          <div className="divide-y divide-forest/15 border-y border-forest/15">
            {t.international.regions.map((r, i) => (
              <Reveal key={i} delay={i * 60} className="py-10 md:py-14 grid grid-cols-12 gap-6 items-center group">
                <div className="col-span-2 md:col-span-1 font-serif text-rust text-2xl md:text-3xl font-light tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="col-span-10 md:col-span-4 font-serif text-3xl md:text-5xl font-light text-forest tracking-tight">
                  {r.name}
                </h3>
                <p className="col-span-12 md:col-span-6 lead">{r.body}</p>
                <div className="col-span-12 md:col-span-1 hidden md:flex justify-end">
                  <ArrowUpRight className="text-forest/40 group-hover:text-rust transition-colors" size={20} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="container-x py-24 md:py-32">
        <Reveal>
          <h2 className="display-2 text-forest max-w-3xl text-balance">{t.international.capabilitiesTitle}</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
          {t.international.capabilities.map((c, i) => (
            <Reveal key={i} delay={i * 80} className="flex gap-6 items-start">
              <span className="font-serif text-rust text-2xl tabular-nums">— {String(i + 1).padStart(2, '0')}</span>
              <p className="lead">{c}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-forest text-sand">
        <div className="container-x py-20 md:py-24 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h3 className="display-3 text-sand max-w-2xl text-balance">{t.home.finalCtaTitle}</h3>
          <Link to="/contacto" className="btn-primary !bg-rust hover:!bg-sand hover:!text-forest self-start" data-testid="intl-final-cta">
            {t.cta.getInTouch} <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
